import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, TextField, InputAdornment, Grid, Chip, Fab,
  FormControl, InputLabel, Select, MenuItem, Skeleton, Paper,
} from '@mui/material';
import { Search, Add } from '@mui/icons-material';
import { useRecipes } from '../../contexts/RecipeContext';
import { CATEGORIES, TAGS, DIFFICULTY } from '../../config';
import RecipeCard from './RecipeCard';

const STATUS_FILTERS = [
  { value: 'all', label: 'All' },
  { value: 'bookmarked', label: '📌 Bookmarked' },
  { value: 'made', label: '✅ Made It' },
  { value: 'starred', label: '⭐ Starred' },
];

export default function RecipeListPage() {
  const navigate = useNavigate();
  const { recipes, recipesLoading } = useRecipes();

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [difficultyFilter, setDifficultyFilter] = useState('');
  const [selectedTags, setSelectedTags] = useState([]);

  const filtered = useMemo(() => {
    return recipes.filter((r) => {
      if (search) {
        const q = search.toLowerCase();
        if (
          !r.title?.toLowerCase().includes(q) &&
          !r.description?.toLowerCase().includes(q) &&
          !r.tags?.some((t) => t.toLowerCase().includes(q))
        ) return false;
      }
      if (statusFilter === 'starred' && !r.starred) return false;
      if (statusFilter === 'bookmarked' && r.status !== 'bookmarked') return false;
      if (statusFilter === 'made' && r.status !== 'made') return false;
      if (categoryFilter && r.category !== categoryFilter) return false;
      if (difficultyFilter && r.difficulty !== difficultyFilter) return false;
      if (selectedTags.length > 0 && !selectedTags.every((t) => r.tags?.includes(t))) return false;
      return true;
    });
  }, [recipes, search, statusFilter, categoryFilter, difficultyFilter, selectedTags]);

  const toggleTag = (tag) => {
    setSelectedTags((prev) =>
      prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag]
    );
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1400, mx: 'auto', pb: { xs: 10, md: 4 } }}>
      <Typography variant="h5" sx={{ mb: 2 }}>My Recipes</Typography>

      {/* Search */}
      <TextField
        placeholder="Search recipes, tags..."
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        fullWidth
        sx={{ mb: 2, '& .MuiOutlinedInput-root': { borderRadius: 3 } }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Search sx={{ color: 'text.secondary' }} />
            </InputAdornment>
          ),
        }}
      />

      {/* Status filter chips */}
      <Box sx={{ display: 'flex', gap: 1, mb: 2, overflowX: 'auto', pb: 0.5 }}>
        {STATUS_FILTERS.map((f) => (
          <Chip
            key={f.value}
            label={f.label}
            clickable
            onClick={() => setStatusFilter(f.value)}
            variant={statusFilter === f.value ? 'filled' : 'outlined'}
            color={statusFilter === f.value ? 'primary' : 'default'}
            sx={{ flexShrink: 0 }}
          />
        ))}
      </Box>

      {/* Dropdown filters */}
      <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Category</InputLabel>
          <Select
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            label="Category"
          >
            <MenuItem value="">All Categories</MenuItem>
            {CATEGORIES.map((c) => (
              <MenuItem key={c.value} value={c.value}>
                {c.emoji} {c.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>Difficulty</InputLabel>
          <Select
            value={difficultyFilter}
            onChange={(e) => setDifficultyFilter(e.target.value)}
            label="Difficulty"
          >
            <MenuItem value="">All Levels</MenuItem>
            {DIFFICULTY.map((d) => (
              <MenuItem key={d.value} value={d.value}>
                {d.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {/* Tag filter */}
      <Box sx={{ display: 'flex', gap: 0.75, mb: 3, flexWrap: 'wrap' }}>
        {TAGS.map((tag) => (
          <Chip
            key={tag}
            label={tag}
            size="small"
            clickable
            onClick={() => toggleTag(tag)}
            variant={selectedTags.includes(tag) ? 'filled' : 'outlined'}
            color={selectedTags.includes(tag) ? 'secondary' : 'default'}
            sx={{ fontSize: '0.75rem' }}
          />
        ))}
      </Box>

      {/* Results count */}
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {filtered.length} recipe{filtered.length !== 1 ? 's' : ''}
      </Typography>

      {recipesLoading ? (
        <Grid container spacing={2}>
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={i}>
              <Skeleton variant="rounded" height={300} />
            </Grid>
          ))}
        </Grid>
      ) : filtered.length === 0 ? (
        <Paper sx={{ p: 6, textAlign: 'center' }}>
          <Typography sx={{ fontSize: '3rem', mb: 2 }}>🔍</Typography>
          <Typography variant="h6" sx={{ mb: 1 }}>No recipes found</Typography>
          <Typography color="text.secondary">
            Try adjusting your filters or add a new recipe.
          </Typography>
        </Paper>
      ) : (
        <Grid container spacing={2}>
          {filtered.map((recipe) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={recipe.id}>
              <RecipeCard recipe={recipe} />
            </Grid>
          ))}
        </Grid>
      )}

      {/* FAB */}
      <Fab
        color="primary"
        onClick={() => navigate('/recipes/new')}
        sx={{
          position: 'fixed',
          bottom: { xs: 80, md: 24 },
          right: 24,
          background: 'linear-gradient(135deg, #f0873c, #c96820)',
        }}
      >
        <Add />
      </Fab>
    </Box>
  );
}
